import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SeoService } from '../../core/seo/seo.service';
import { RouterLink } from '@angular/router';
import { combineLatest, switchMap, tap } from 'rxjs';
import { PropertyDataProvider } from '../../core/data/property-data.provider';
import {
  BUILD_BAND_BG,
  CONSTRUCTION_BG,
  fmt1,
  fmt2,
  fmtEurPerM2,
  fmtInt,
  PROPERTY_TYPE_BG,
} from '../../core/i18n/labels';
import { BreakdownRow, CityDetail, Granularity, NeighborhoodStats, SeriesPoint } from '../../core/models/domain.models';
import { lastN } from '../../core/stats/metrics';
import { GaugeChartComponent, NamedSeries, TrendChartComponent } from '../../shared/charts.components';
import { NeighborhoodMapComponent } from '../../shared/neighborhood-map.component';
import { Bg1Pipe, Bg2Pipe, BgIntPipe, EurM2Pipe, PctPipe } from '../../shared/format.pipes';
import {
  DeltaChipComponent,
  GranularityToggleComponent,
  KpiCardComponent,
  MarketNoticeComponent,
  MortgageCalcComponent,
  PageHeaderComponent,
  RangeToggleComponent,
} from '../../shared/ui.components';

@Component({
  selector: 'app-city-detail-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    PageHeaderComponent,
    MarketNoticeComponent,
    GranularityToggleComponent,
    RangeToggleComponent,
    KpiCardComponent,
    DeltaChipComponent,
    TrendChartComponent,
    GaugeChartComponent,
    NeighborhoodMapComponent,
    MortgageCalcComponent,
    EurM2Pipe,
    BgIntPipe,
    Bg1Pipe,
    Bg2Pipe,
    PctPipe,
  ],
  templateUrl: './city-detail.page.html',
  styleUrl: './city-detail.page.scss',
})
export class CityDetailPage {
  private readonly data = inject(PropertyDataProvider);
  private readonly seo = inject(SeoService);

  /** Route param via withComponentInputBinding. */
  slug = input.required<string>();

  readonly granularity = signal<Granularity>('month');
  readonly rangeMonths = signal<number>(24);
  readonly nbhdView = signal<'table' | 'map'>('table');
  readonly nbhdQuery = signal<string>('');
  readonly nbhdSortKey = signal<'name' | 'distance' | 'price' | 'rent' | 'yield' | 'yoy' | 'listings'>('price');
  readonly nbhdSortDir = signal<'asc' | 'desc'>('desc');

  readonly overview = toSignal(this.data.overview('month'));
  readonly benchmarkMortgageRate = computed(() => this.overview()?.mortgageRate.headline ?? 2.45);

  readonly detail = toSignal(
    combineLatest([toObservable(this.slug), toObservable(this.granularity)]).pipe(
      switchMap(([slug, g]) => this.data.cityDetail(slug, g)),
      // Set title/canonical/OG as a side effect of the same async resolution
      // that produces the data — an `effect()` reading the resulting signal
      // isn't guaranteed to flush before SSR serializes the response, so the
      // per-city <head> tags were silently missing from the rendered HTML.
      tap((d) => {
        const titlePrice = d.price.headline ? `${Math.round(d.price.headline)} €/м²` : 'Пазарен анализ';
        const pageTitle = `Цени на имоти в ${d.city.name} 2026 — ${titlePrice} | Имотомер`;
        const txSnippet = d.transactions.headline ? `${fmtInt(d.transactions.headline)} вписани сделки/трим., ` : '';
        const pageDesc = `Подробен пазарен анализ за ${d.city.name}: медианна офертна цена ${d.price.headline ? Math.round(d.price.headline) + ' €/м²' : '—'}, ${txSnippet}наеми, доходност ${fmt2(d.rentalYield.headline)}%, цени по квартали и индекс на прегряване.`;

        const faqData = this.buildFaq(d);

        this.seo.set({
          title: pageTitle,
          description: pageDesc,
          path: `/grad/${d.city.slug}`,
          keywords: [
            `цени на имоти ${d.city.name}`,
            `цени на имоти ${d.city.name} 2026`,
            `цена на квадратен метър ${d.city.name}`,
            `сделки с имоти ${d.city.name}`,
            `наеми ${d.city.name}`,
            `доходност ${d.city.name}`,
            `квартали ${d.city.name} цени`,
            `имотен пазар ${d.city.name}`,
            'имотомер',
            'imotomer',
          ],
          jsonLd: [
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Начало',
                  item: 'https://real-estate.tur92.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Градове',
                  item: 'https://real-estate.tur92.com/gradove',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: d.city.name,
                  item: `https://real-estate.tur92.com/grad/${d.city.slug}`,
                },
              ],
            },
            {
              '@context': 'https://schema.org',
              '@type': 'Dataset',
              name: `Цени на имоти и пазарна статистика за ${d.city.name}`,
              description: `Официални статистически данни за цени на квадратен метър, вписани сделки от Агенция по вписванията, наеми и достъпност в ${d.city.name}.`,
              spatialCoverage: {
                '@type': 'Place',
                name: d.city.name,
                geo: {
                  '@type': 'GeoCoordinates',
                  latitude: d.city.lat,
                  longitude: d.city.lng,
                },
                address: {
                  '@type': 'PostalAddress',
                  addressLocality: d.city.name,
                  addressCountry: 'BG',
                },
              },
              variableMeasured: [
                'Медианна офертна цена на жилищен имот (€/м²)',
                'Тримесечни вписани сделки (Агенция по вписванията)',
                'Брутна годишна доходност от наем',
                'Индекс на прегряване на пазара',
              ],
            },
            {
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqData.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: f.a,
                },
              })),
            },
          ],
        });
      }),
    ),
  );

  readonly faqItems = computed(() => {
    const d = this.detail();
    return d ? this.buildFaq(d) : [];
  });

  private buildFaq(d: CityDetail): Array<{ q: string; a: string }> {
    const priceText = d.price.headline ? `${Math.round(d.price.headline)} €/м²` : 'няма достатъчно актуални данни';
    const yoyText = d.price.yoyPct !== null ? ` с годишно изменение от ${d.price.yoyPct > 0 ? '+' : ''}${d.price.yoyPct}% (ГоГ)` : '';
    const txCount = d.transactions.headline ? `${fmtInt(d.transactions.headline)} сделки с имоти` : 'няма данни';
    const rentVal = d.rent.headline ? `${fmt2(d.rent.headline)} €/м²/месец` : '—';
    const yieldVal = d.rentalYield.headline ? `${fmt2(d.rentalYield.headline)}%` : '—';
    const heatVal = d.overheating.score !== null ? `${d.overheating.score}/100` : 'балансиран';

    return [
      {
        q: `Каква е средната цена на квадратен метър в ${d.city.name} през 2026 г.?`,
        a: `Медианната офертна цена на жилищните имоти за продажба в ${d.city.name} възлиза на ${priceText}${yoyText}.`,
      },
      {
        q: `Колко сделки с недвижими имоти се сключват в ${d.city.name}?`,
        a: `По официални данни на Агенция по вписванията (Имотен регистър) за последното тримесечие в ${d.city.name} са вписани ${txCount}.`,
      },
      {
        q: `Каква е брутната доходност от отдаване под наем в ${d.city.name}?`,
        a: `Средната годишна доходност от наем в ${d.city.name} е около ${yieldVal} при медиаен наем от ${rentVal}.`,
      },
      {
        q: `Прегрят ли е пазарът на имоти в ${d.city.name}?`,
        a: `Индексът на прегряване за ${d.city.name} е ${heatVal}, отразяващ баланса между динамиката на цените, доходите на населението и наемите.`,
      },
    ];
  }

  readonly fmtEurPerM2 = fmtEurPerM2;
  readonly fmtInt = fmtInt;
  readonly fmt1 = fmt1;
  readonly fmt2 = fmt2;

  ranged(series: SeriesPoint[]): SeriesPoint[] {
    const n = this.rangeMonths();
    return lastN(series, this.granularity() === 'year' ? Math.ceil(n / 12) : n);
  }

  priceChartSeries(d: CityDetail): NamedSeries[] {
    const base: NamedSeries[] = [
      { name: 'Медиана €/м²', series: this.ranged(d.price.series) },
      { name: 'Сделки / трим.', series: this.ranged(d.transactions.series) },
    ];
    return d.priceForecast.length
      ? [...base, { name: 'Прогноза (линеен тренд)', series: d.priceForecast, projected: true }]
      : base;
  }

  corrLabel(dir: 'negative' | 'positive' | 'neutral'): string {
    return dir === 'negative' ? 'Обратна' : dir === 'positive' ? 'Права' : 'Неутрална';
  }

  typeLabel(row: BreakdownRow): string {
    return PROPERTY_TYPE_BG[row.key as keyof typeof PROPERTY_TYPE_BG] ?? row.key;
  }
  constructionLabel(row: BreakdownRow): string {
    return CONSTRUCTION_BG[row.key as keyof typeof CONSTRUCTION_BG] ?? row.key;
  }
  bandLabel(row: BreakdownRow): string {
    return BUILD_BAND_BG[row.key] ?? row.key;
  }

  readonly filteredNeighborhoods = computed<NeighborhoodStats[]>(() => {
    const d = this.detail();
    if (!d) return [];
    const q = this.nbhdQuery().trim().toLowerCase();
    let list = d.neighborhoods;
    if (q) {
      list = list.filter((n) => n.name.toLowerCase().includes(q));
    }
    const key = this.nbhdSortKey();
    const dir = this.nbhdSortDir() === 'asc' ? 1 : -1;

    return [...list].sort((a, b) => {
      if (key === 'name') {
        return a.name.localeCompare(b.name, 'bg') * dir;
      }
      const val = (n: NeighborhoodStats): number => {
        switch (key) {
          case 'distance': return n.distanceFromCenterKm ?? 999;
          case 'price': return n.medianSaleEurPerM2;
          case 'rent': return n.medianRentEurPerM2;
          case 'yield': return n.rentalYieldPct ?? -999;
          case 'yoy': return n.yoyPct ?? -999;
          case 'listings': return n.activeListings;
        }
      };
      return (val(a) - val(b)) * dir;
    });
  });

  sortNbhd(key: 'name' | 'distance' | 'price' | 'rent' | 'yield' | 'yoy' | 'listings'): void {
    if (this.nbhdSortKey() === key) {
      this.nbhdSortDir.update((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      this.nbhdSortKey.set(key);
      this.nbhdSortDir.set(key === 'name' || key === 'distance' ? 'asc' : 'desc');
    }
  }

  nbhdArrow(key: string): string {
    return this.nbhdSortKey() === key ? (this.nbhdSortDir() === 'asc' ? ' ↑' : ' ↓') : '';
  }
}

